import React from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { FlashpackItem, FlashpackType, MinMaxPair } from "../../common/api";
import { ClientConfigContext } from "../../common/context/ClientConfigContext";
import { getMathOperators, normalizeMathOperator } from "../../common/utils";
import { ConfirmItemRemovalModal } from "./ConfirmItemRemovalModal";

const Subhead = styled.span`
  color: #828282;
  font-size: 13px;
`;

const Warning = styled.div`
  color: #eb5757;
  font-size: 13px;
`;

// The math flashcards' "small_cards_gradient" (see math_trainer_v10 get_media_lists):
// vertical #FF0099 -> #FFC700.
const MATH_GRADIENT = "linear-gradient(to bottom, #ff0099, #ffc700)";

const ProblemChip = styled.div`
  display: inline-block;
  background: ${MATH_GRADIENT};
  color: white;
  font-family: "Inter";
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px;
  padding: 4px 10px;
  margin: 4px;
  cursor: pointer;
`;

const ProblemCard = styled.div`
  background: ${MATH_GRADIENT};
  border-radius: 16px;
  color: white;
  font-family: "Inter";
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  height: 150px;
  font-size: 56px;
`;

const OperandInput = styled.input`
  width: 96px;
  height: 88px;
  text-align: center;
  background: transparent;
  color: white;
  caret-color: white;
  font-size: 56px;
  font-weight: 600;
  outline: none;
  border: ${({ value }) =>
    value ? "none" : "2px dashed rgba(255, 255, 255, 0.85)"};
  border-radius: 8px;
`;

const OperatorButton = styled.button`
  width: 44px;
  height: 44px;
  margin: 0 4px;
  border-radius: 8px;
  font-family: "Inter";
  font-weight: 600;
  font-size: 22px;
  border: 1px solid ${({ $selected }) => ($selected ? "#2f80ed" : "#c4c4c4")};
  background-color: ${({ $selected }) => ($selected ? "#2f80ed" : "white")};
  color: ${({ $selected }) => ($selected ? "white" : "#333")};
`;

const AddCardButton = styled(Button)`
  font-weight: 600;
  background-color: #2f80ed;
  border-color: #2f80ed;
  &:disabled {
    background-color: #e0e0e0;
    border-color: #e0e0e0;
    color: #9e9e9e;
    opacity: 1;
  }
`;

// Mirror of the backend's is_row_semantically_valid: division must have a
// non-zero divisor and yield a whole number.
const isOperationValid = (operator: string, x: number, y: number) =>
  operator !== "÷" || (y !== 0 && x % y === 0);

export const MathFlashpackBuilder = ({
  flashpackType,
  deckItems,
  setDeckItems,
  setInvalidDeckErrorMessage,
}: {
  flashpackType: FlashpackType;
  deckItems: FlashpackItem[];
  setDeckItems: (newDeckItems: FlashpackItem[]) => void;
  setInvalidDeckErrorMessage: (message: string | null) => void;
}) => {
  const { t } = useTranslation();
  const { clientConfig } = React.useContext(ClientConfigContext);
  const [minMaxCards, setMinMaxCards] = React.useState<MinMaxPair>();
  const operators = React.useMemo(
    () => getMathOperators(flashpackType),
    [flashpackType]
  );
  const [operator, setOperator] = React.useState<string>(operators[0]);
  const [first, setFirst] = React.useState("");
  const [second, setSecond] = React.useState("");
  const [pendingItemRemoval, setPendingItemRemoval] =
    React.useState<FlashpackItem>();
  const firstRef = React.useRef<HTMLInputElement>(null);
  const secondRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setMinMaxCards(
      clientConfig.flashpack_min_max_card_constraints[flashpackType]
    );
  }, [clientConfig, flashpackType]);

  // Keep the selected operator valid if the available set changes.
  React.useEffect(() => setOperator(operators[0]), [operators]);

  React.useEffect(() => {
    if (minMaxCards === undefined) {
      setInvalidDeckErrorMessage(t("flashpacks_edit_unknown_type"));
    } else if (deckItems.length < minMaxCards.min) {
      setInvalidDeckErrorMessage(
        t("flashpacks_edit_less_than_min_cards", { count: minMaxCards.min })
      );
    } else if (deckItems.length > minMaxCards.max) {
      setInvalidDeckErrorMessage(
        t("flashpacks_edit_more_than_max_cards", { count: minMaxCards.max })
      );
    } else {
      setInvalidDeckErrorMessage(null);
    }
  }, [t, setInvalidDeckErrorMessage, deckItems, minMaxCards]);

  React.useEffect(() => firstRef.current?.focus(), []);

  const sanitize = (value: string) => value.replace(/\D/g, "").slice(0, 2);

  // Typing an operator (after the first or second digit) selects it when the pack
  // allows a choice and jumps to the second operand, so a card can be entered
  // without lifting fingers off the keyboard.
  const onFirstOperandChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = sanitize(e.target.value);
    setFirst(digits);
    const typedOperator =
      e.target.value
        .split("")
        .map(normalizeMathOperator)
        .find((op) => op !== null) ?? null;
    if (typedOperator === null) {
      return;
    }
    if (operators.includes(typedOperator)) {
      setOperator(typedOperator);
    }
    if (digits.length > 0) {
      secondRef.current?.focus();
    }
  };

  const canAddCard =
    first.length > 0 &&
    second.length > 0 &&
    minMaxCards !== undefined &&
    deckItems.length < minMaxCards.max &&
    isOperationValid(operator, parseInt(first, 10), parseInt(second, 10));

  // Warn when a division card was entered that doesn't divide evenly.
  const showDivisionWarning =
    operator === "÷" &&
    first.length > 0 &&
    second.length > 0 &&
    !isOperationValid(operator, parseInt(first, 10), parseInt(second, 10));

  const onAddCard = () => {
    if (!canAddCard) {
      return;
    }
    const item = `${parseInt(first, 10)} ${operator} ${parseInt(second, 10)}`;
    if (!deckItems.some((deckItem) => deckItem.item === item)) {
      setDeckItems([...deckItems, { item, image_url: null }]);
    }
    setFirst("");
    setSecond("");
    firstRef.current?.focus();
  };

  const onCancelItemRemoval = () => setPendingItemRemoval(undefined);

  const onConfirmItemRemoval = () => {
    if (pendingItemRemoval) {
      setDeckItems(
        deckItems.filter((d) => d.item !== pendingItemRemoval.item)
      );
      setPendingItemRemoval(undefined);
    }
  };

  return (
    <>
    <Container>
      <Row className="justify-content-center">
        <Col xs={10} md={8} lg={6}>
          {minMaxCards !== undefined && (
            <div className="text-center">
              <Subhead>
                {deckItems.length < minMaxCards.min
                  ? t("flashpacks_min_cards", { count: minMaxCards.min })
                  : t("flashpacks_max_cards", { count: minMaxCards.max })}
              </Subhead>
            </div>
          )}

          {/* Added problems */}
          <div className="text-center my-2">
            {deckItems.map((item) => (
              <ProblemChip
                key={item.item}
                onClick={() => setPendingItemRemoval(item)}
              >
                {item.item}
              </ProblemChip>
            ))}
          </div>

          {/* Operator picker (only for the umbrella Math type, which spans
              all operators; single-operation types have a fixed operator). */}
          {flashpackType === FlashpackType.Math && (
            <div className="text-center mb-2">
              {operators.map((op) => (
                <OperatorButton
                  key={op}
                  $selected={op === operator}
                  onClick={() => setOperator(op)}
                >
                  {op}
                </OperatorButton>
              ))}
            </div>
          )}

          {/* Problem entry card */}
          <ProblemCard>
            <OperandInput
              ref={firstRef}
              inputMode="numeric"
              value={first}
              onChange={onFirstOperandChange}
            />
            <span>{operator}</span>
            <OperandInput
              ref={secondRef}
              inputMode="numeric"
              value={second}
              onChange={(e) => setSecond(sanitize(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onAddCard();
                }
              }}
            />
          </ProblemCard>

          {showDivisionWarning && (
            <Warning className="text-center mt-2">
              {parseInt(second, 10) === 0
                ? t("flashpacks_division_by_zero")
                : t("flashpacks_division_must_divide_evenly")}
            </Warning>
          )}

          <div className="d-grid mt-3">
            <AddCardButton disabled={!canAddCard} onClick={onAddCard}>
              {t("flashpacks_add_card")}
            </AddCardButton>
          </div>
        </Col>
      </Row>
    </Container>

    <ConfirmItemRemovalModal
      item={pendingItemRemoval}
      onCancel={onCancelItemRemoval}
      onConfirm={onConfirmItemRemoval}
    />
    </>
  );
};
